import {
  Navigate,
  Outlet,
  createFileRoute,
  notFound,
  redirect,
} from '@tanstack/react-router'

import { hasPermissions } from '@/access-control/hasPermissions'
import { AppManagerFetch } from '@/app/AppManagerFetch'
import { AppManagerIdentify } from '@/app/AppManagerIdentify'
import { AppManagerSSO } from '@/app/AppManagerSSO'
import { LoadingIndicator } from '@/core/components/LoadingIndicator'
import { FEATURE_COMPANY } from '@/core/constants/features'
import { useNetworkNotifications } from '@/core/hooks/useNetworkNotifications'
import keycloak from '@/core/keycloak'
import useFeatures from '@/core/utils/features/useFeatures'
import { companyQueries } from '@/http/api/company'
import { usersQueries } from '@/http/api/users'
import { WebPushRegistration } from '@/push-notifications/components/WebPushRegistration/WebPushRegistration'

export const Route = createFileRoute('/company')({
  beforeLoad: async ({ context: { queryClient } }) => {
    if (!keycloak.authenticated) {
      throw redirect({
        href: keycloak.createLoginUrl({ redirectUri: window.location.href }),
      })
    }

    const user = await queryClient.ensureQueryData(
      usersQueries.usersCurrentDetails(),
    )
    if (!user) {
      throw Error('User not found')
    }

    if (!hasPermissions(user, { to: 'access-company' })) {
      throw notFound()
    }

    return { user }
  },

  loader: async ({ context: { queryClient } }) => {
    await queryClient.ensureQueryData(companyQueries.detail())
  },
  component: CompanyLayout,
  pendingComponent: () => <LoadingIndicator size="full-screen" />,
})

function CompanyLayout() {
  useNetworkNotifications()
  const hasCompanyFeature = useFeatures(FEATURE_COMPANY)

  if (!hasCompanyFeature) {
    return <Navigate to="/home" replace />
  }

  return (
    <AppManagerSSO>
      <AppManagerFetch>
        <AppManagerIdentify>
          <WebPushRegistration />
          <Outlet />
        </AppManagerIdentify>
      </AppManagerFetch>
    </AppManagerSSO>
  )
}
